// client/src/components/VouchersTab.jsx
// "שוברים" tab on CampDetailPage. Three regions:
//   - Stats strip: ACTIVE count, REDEEMED count, ₪outstanding
//   - Toolbar: status chip filters + search-by-code + create-batch CTA
//             + print-active link
//   - List: rows with code (mono, 3-3-3 grouping), amount, batch label,
//           status pill, redemption info or cancel button
// The cancel action is hidden for non-ACTIVE rows (REDEEMED is locked,
// CANCELLED is idempotent).

import { useState } from 'react';
import {
  useVouchers,
  useVoucherStats,
  useVoucherBatches,
  useCreateVoucherBatch,
  useCancelVoucher,
  useCancelVoucherBatch,
} from '../hooks/useVouchers.js';
import Spinner from './Spinner.jsx';
import Modal from './Modal.jsx';
import { formatDate, formatDateTime, formatMoney } from '../lib/format.js';
import {
  ArrowDownTrayIcon,
  BanknotesIcon,
  CreditCardIcon,
  ArchiveBoxXMarkIcon,
  ExclamationTriangleIcon,
} from './Icons.jsx';

const PAGE_SIZE = 100;

export default function VouchersTab({ campId, currency }) {
  const [filters, setFilters] = useState({ status: 'ACTIVE', q: '' });
  const [page, setPage] = useState(0);
  const [creating, setCreating] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [cancellingBatch, setCancellingBatch] = useState(false);
  const [cancelling, setCancelling] = useState(null); // { id, code, amount }

  const query = {
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.q ? { q: filters.q } : {}),
  };

  const vouchersQ = useVouchers(campId, query);
  const statsQ    = useVoucherStats(campId);
  const createMut = useCreateVoucherBatch(campId);
  const cancelMut = useCancelVoucher(campId);

  const list  = vouchersQ.data;
  const total = list?.total ?? 0;
  const hasMore = total > (page + 1) * PAGE_SIZE;

  function setStatus(status) {
    setFilters({ ...filters, status });
    setPage(0);
  }

  return (
    <div className="space-y-4">
      <StatsStrip stats={statsQ.data} currency={currency} />

      <Toolbar
        filters={filters}
        onStatusChange={setStatus}
        onQChange={(q) => { setFilters({ ...filters, q }); setPage(0); }}
        total={total}
        onCreate={() => setCreating(true)}
        onPrint={() => setPrinting(true)}
        onCancelBatch={() => setCancellingBatch(true)}
      />

      {vouchersQ.isLoading && !list ? (
        <Spinner label="טוען שוברים..." />
      ) : vouchersQ.isError ? (
        <div className="card-danger p-4 text-joy-900 text-sm">
          שגיאה: {vouchersQ.error.message}
        </div>
      ) : list.vouchers.length === 0 ? (
        <EmptyState hasFilters={!!(filters.status || filters.q)} onCreate={() => setCreating(true)} />
      ) : (
        <>
          <VoucherList
            vouchers={list.vouchers}
            currency={currency}
            onCancel={(v) => setCancelling({ id: v.id, code: v.code, amount: v.amount })}
          />
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={total}
            hasMore={hasMore}
            onPrev={() => setPage((p) => Math.max(0, p - 1))}
            onNext={() => setPage((p) => p + 1)}
          />
        </>
      )}

      <CreateBatchDialog
        open={creating}
        onClose={() => setCreating(false)}
        onSubmit={(body) => createMut.mutateAsync(body)}
        pending={createMut.isPending}
        currency={currency}
        campId={campId}
      />

      <Modal
        open={!!cancelling}
        onClose={() => setCancelling(null)}
        title="ביטול שובר"
      >
        {cancelling && (
          <CancelForm
            voucher={cancelling}
            currency={currency}
            pending={cancelMut.isPending}
            onCancel={() => setCancelling(null)}
            onConfirm={async (reason) => {
              await cancelMut.mutateAsync({ voucherId: cancelling.id, reason });
              setCancelling(null);
            }}
          />
        )}
      </Modal>

      <PrintBatchDialog
        open={printing}
        onClose={() => setPrinting(false)}
        campId={campId}
        currency={currency}
      />

      <CancelBatchDialog
        open={cancellingBatch}
        onClose={() => setCancellingBatch(false)}
        campId={campId}
        currency={currency}
      />
    </div>
  );
}

/* ───── Cancel-whole-batch picker ───────────────────────────── */

function CancelBatchDialog({ open, onClose, campId, currency }) {
  const batchesQ = useVoucherBatches(campId, { enabled: open });
  const cancelMut = useCancelVoucherBatch(campId);
  const [selected, setSelected] = useState(null); // a batch row, or null = pick
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);

  const batches = batchesQ.data?.batches || [];
  const activeBatches = batches.filter((b) => b.active > 0);

  function close() {
    setSelected(null); setReason(''); setError(null);
    onClose();
  }

  async function confirm() {
    setError(null);
    try {
      await cancelMut.mutateAsync({
        batchLabel: selected.batchLabel ?? null,
        createdAt: new Date(selected.createdAt).toISOString(),
        reason: reason.trim() || undefined,
      });
      close();
    } catch (err) {
      setError(err.message || err.code || 'שגיאה לא צפויה');
    }
  }

  return (
    <Modal open={open} onClose={close} title="ביטול סדרה שלמה">
      {batchesQ.isLoading ? (
        <Spinner label="טוען סדרות..." />
      ) : batchesQ.isError ? (
        <div className="card-danger p-3 text-sm text-joy-900">
          שגיאה בטעינת סדרות: {batchesQ.error.message}
        </div>
      ) : selected ? (
        <div className="space-y-4">
          <div className="rounded-2xl bg-joy-50 border border-joy-200 p-4 text-sm text-joy-900 flex items-start gap-2">
            <ExclamationTriangleIcon className="w-5 h-5 shrink-0 mt-0.5" />
            <span>
              לבטל את כל <strong className="num">{selected.active.toLocaleString('he-IL')}</strong>{' '}
              השוברים הפעילים בסדרה{' '}
              <strong>{selected.batchLabel || 'ללא תווית'}</strong>{' '}
              (<span className="num">{formatDateTime(selected.createdAt)}</span>)?
              שוברים שכבר מומשו לא יושפעו, ואי אפשר לשחזר ביטול.
            </span>
          </div>
          {error && (
            <div className="rounded-2xl bg-joy-50 border border-joy-200 text-joy-900 px-4 py-3 text-sm font-medium">
              {error}
            </div>
          )}
          <label className="block">
            <span className="label">סיבה (אופציונלי)</span>
            <input
              type="text"
              className="input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={200}
            />
          </label>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={() => { setSelected(null); setError(null); }}>
              חזרה
            </button>
            <button type="button" className="btn-danger" disabled={cancelMut.isPending} onClick={confirm}>
              {cancelMut.isPending ? 'מבטל...' : `כן, בטל ${selected.active.toLocaleString('he-IL')} שוברים`}
            </button>
          </div>
        </div>
      ) : activeBatches.length === 0 ? (
        <div className="p-4 text-sm text-ink-600">
          אין כרגע שוברים פעילים לביטול.
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-ink-600">
            בחרו סדרה כדי לבטל את כל השוברים <strong>הפעילים</strong> שנשארו בה
            בבת אחת. שוברים שכבר מומשו לא יושפעו.
          </p>
          <ul className="card divide-y divide-ink-100 overflow-hidden">
            {activeBatches.map((b) => (
              <li key={`${b.batchLabel ?? '__null__'}@@${b.createdAt}`}>
                <button
                  type="button"
                  onClick={() => setSelected(b)}
                  className="w-full text-start px-4 py-3 hover:bg-joy-50 transition-colors flex items-center gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-ink-900">
                      {b.batchLabel || <span className="text-ink-400">ללא תווית</span>}
                    </div>
                    <div className="text-xs text-ink-500 mt-0.5 num">
                      {formatDateTime(b.createdAt)} · ערך {formatMoney(b.amount, currency)} לשובר
                    </div>
                  </div>
                  <div className="text-end">
                    <div className="font-bold text-mint-700 num">{b.active.toLocaleString('he-IL')}</div>
                    <div className="text-[11px] text-ink-500">פעילים</div>
                  </div>
                  <ArchiveBoxXMarkIcon className="w-4 h-4 text-joy-400 shrink-0" />
                </button>
              </li>
            ))}
          </ul>
          <div className="flex justify-end pt-1">
            <button type="button" onClick={close} className="btn-ghost btn-sm">סגירה</button>
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ───── Print-batch picker ──────────────────────────────────── */

function PrintBatchDialog({ open, onClose, campId, currency }) {
  const batchesQ = useVoucherBatches(campId, { enabled: open });
  const batches = batchesQ.data?.batches || [];
  const activeBatches = batches.filter((b) => b.active > 0);

  // Pass a batch object to print exactly that run (name + date → createdAt
  // pins the one create call), or null to print every active voucher.
  function openPrint(batch) {
    const qs = new URLSearchParams({ status: 'ACTIVE' });
    if (batch) {
      if (batch.batchLabel) qs.set('batch', batch.batchLabel);
      if (batch.createdAt)  qs.set('createdAt', new Date(batch.createdAt).toISOString());
    }
    window.open(`/api/camps/${campId}/vouchers/print.html?${qs.toString()}`, '_blank', 'noopener');
    onClose();
  }

  return (
    <Modal open={open} onClose={onClose} title="הדפסת שוברים — איזו סדרה?">
      {batchesQ.isLoading ? (
        <Spinner label="טוען סדרות..." />
      ) : batchesQ.isError ? (
        <div className="card-danger p-3 text-sm text-joy-900">
          שגיאה בטעינת סדרות: {batchesQ.error.message}
        </div>
      ) : activeBatches.length === 0 ? (
        <div className="p-4 text-sm text-ink-600">
          אין כרגע שוברים פעילים להדפסה. צרו סדרה חדשה ואז תוכלו להדפיס אותה כאן.
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-ink-600">
            ההדפסה כוללת רק את השוברים <strong>הפעילים</strong> בסדרה
            שתבחרו. שוברים שכבר מומשו או בוטלו לא נכללים.
          </p>
          <ul className="card divide-y divide-ink-100 overflow-hidden">
            {activeBatches.map((b) => (
              <li key={`${b.batchLabel ?? '__null__'}@@${b.createdAt}`}>
                <button
                  type="button"
                  onClick={() => openPrint(b)}
                  className="w-full text-start px-4 py-3 hover:bg-brand-50 transition-colors flex items-center gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-ink-900">
                      {b.batchLabel || <span className="text-ink-400">ללא תווית</span>}
                    </div>
                    <div className="text-xs text-ink-500 mt-0.5 num">
                      {formatDateTime(b.createdAt)} · ערך {formatMoney(b.amount, currency)} לשובר
                    </div>
                  </div>
                  <div className="text-end">
                    <div className="font-bold text-mint-700 num">{b.active.toLocaleString('he-IL')}</div>
                    <div className="text-[11px] text-ink-500">פעילים</div>
                  </div>
                  <ArrowDownTrayIcon className="w-4 h-4 text-ink-400 shrink-0" />
                </button>
              </li>
            ))}
          </ul>
          <div className="flex items-center justify-between gap-2 pt-1">
            {activeBatches.length > 1 ? (
              <button
                type="button"
                onClick={() => openPrint(null)}
                className="btn-secondary btn-sm"
              >
                הדפסת כל השוברים הפעילים
              </button>
            ) : <span />} {/* null → no createdAt → every active voucher */}
            <button type="button" onClick={onClose} className="btn-ghost btn-sm">סגירה</button>
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ───── Stats strip ──────────────────────────────────────────── */

function StatsStrip({ stats, currency }) {
  const safe = stats || { active: { count: 0, sum: 0 }, redeemed: { count: 0, sum: 0 }, cancelled: { count: 0, sum: 0 } };
  return (
    <div className="grid grid-cols-3 gap-3">
      <div className="kpi relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 strap-earn" />
        <div className="kpi-label">שוברים פעילים</div>
        <div className="kpi-value">{safe.active.count.toLocaleString('he-IL')}</div>
        <div className="kpi-hint num">{formatMoney(safe.active.sum, currency)} פוטנציאל יתרה</div>
      </div>
      <div className="kpi relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 bg-brand-gradient h-1.5" />
        <div className="kpi-label">מומשו</div>
        <div className="kpi-value">{safe.redeemed.count.toLocaleString('he-IL')}</div>
        <div className="kpi-hint num">{formatMoney(safe.redeemed.sum, currency)} סך שניתן</div>
      </div>
      <div className="kpi relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 bg-gradient-to-l from-clay-400 to-joy-500 h-1.5" />
        <div className="kpi-label">בוטלו</div>
        <div className="kpi-value">{safe.cancelled.count.toLocaleString('he-IL')}</div>
        <div className="kpi-hint num">{formatMoney(safe.cancelled.sum, currency)}</div>
      </div>
    </div>
  );
}

/* ───── Toolbar ──────────────────────────────────────────────── */

function Toolbar({ filters, onStatusChange, onQChange, total, onCreate, onPrint, onCancelBatch }) {
  return (
    <div className="card p-3 sm:p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <SearchIcon className="w-4 h-4 absolute top-1/2 -translate-y-1/2 start-3 text-ink-400" />
          <input
            type="text"
            inputMode="numeric"
            aria-label="חיפוש לפי קוד שובר"
            className="input ps-9"
            dir="ltr"
            value={filters.q}
            onChange={(e) => onQChange(e.target.value.replace(/\D/g, ''))}
            maxLength={9}
          />
        </div>
        <div className="ms-auto flex gap-2">
          <button type="button" className="btn-secondary" onClick={onCancelBatch}>
            <ArchiveBoxXMarkIcon className="w-4 h-4" />
            ביטול סדרה
          </button>
          <button type="button" className="btn-secondary" onClick={onPrint}>
            <ArrowDownTrayIcon className="w-4 h-4" />
            הדפסה
          </button>
          <button type="button" className="btn-pop" onClick={onCreate}>
            <PlusIcon className="w-4 h-4" />
            סדרה חדשה
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 items-center">
        <span className="text-xs font-bold text-ink-500 uppercase tracking-wider me-1">סטטוס</span>
        <StatusChip active={filters.status === 'ACTIVE'}    label="פעילים" tone="mint" onClick={() => onStatusChange('ACTIVE')} />
        <StatusChip active={filters.status === 'REDEEMED'}  label="מומשו"  tone="brand" onClick={() => onStatusChange('REDEEMED')} />
        <StatusChip active={filters.status === 'CANCELLED'} label="בוטלו" tone="joy"  onClick={() => onStatusChange('CANCELLED')} />
        <StatusChip active={!filters.status}                label="הכל"   onClick={() => onStatusChange('')} />
        <span className="ms-auto text-xs text-ink-500">
          סה״כ <strong className="text-ink-700 num">{total.toLocaleString('he-IL')}</strong>
        </span>
      </div>
    </div>
  );
}

function StatusChip({ active, label, onClick, tone }) {
  let cls = active ? 'chip-active' : 'chip';
  if (active && tone === 'mint') cls = 'chip bg-mint-600 border-mint-600 text-white hover:bg-mint-700 shadow-sm';
  if (active && tone === 'brand') cls = 'chip bg-brand-600 border-brand-600 text-white hover:bg-brand-700 shadow-sm';
  if (active && tone === 'joy')  cls = 'chip bg-joy-600  border-joy-600  text-white hover:bg-joy-700  shadow-sm';
  return <button type="button" onClick={onClick} className={cls}>{label}</button>;
}

/* ───── List + row ───────────────────────────────────────────── */

function VoucherList({ vouchers, currency, onCancel }) {
  return (
    <div className="card overflow-hidden">
      <ul className="divide-y divide-ink-100">
        {vouchers.map((v) => (
          <VoucherRow key={v.id} v={v} currency={currency} onCancel={onCancel} />
        ))}
      </ul>
    </div>
  );
}

/** "123456789" → "123 456 789" — easier to read on a printed voucher. */
function groupCode(code) {
  return String(code).replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3');
}

function VoucherRow({ v, currency, onCancel }) {
  const isActive    = v.status === 'ACTIVE';
  const isRedeemed  = v.status === 'REDEEMED';
  const isCancelled = v.status === 'CANCELLED';
  return (
    <li className="p-4 hover:bg-ink-50/60 transition-colors flex items-center gap-3 flex-wrap">
      <div className="font-mono num tracking-[0.18em] text-lg font-bold text-ink-900 shrink-0" dir="ltr">
        {groupCode(v.code)}
      </div>
      <div className="flex-1 min-w-[180px]">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-display font-extrabold text-xl text-brand-700 num">
            {formatMoney(v.amount, currency)}
          </span>
          {isActive    && <span className="badge-mint">פעיל</span>}
          {isRedeemed  && <span className="badge-indigo">מומש</span>}
          {isCancelled && <span className="badge-joy">בוטל</span>}
          {v.batchLabel && <span className="badge-slate">{v.batchLabel}</span>}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-ink-500">
          <span className="num">נוצר {formatDate(v.createdAt)}</span>
          {v.expiresAt && (
            <span className="num">תוקף עד {formatDate(v.expiresAt)}</span>
          )}
          {isRedeemed && v.redeemedBy && (
            <span>
              מומש ע״י <strong className="text-ink-700">{v.redeemedBy.firstName} {v.redeemedBy.lastName}</strong>
              {v.redeemedBy.className && <span className="text-ink-400"> · {v.redeemedBy.className}</span>}
              <span className="text-ink-400 num"> · {formatDateTime(v.redeemedAt)}</span>
            </span>
          )}
          {isCancelled && (
            <span>
              בוטל <span className="num">{formatDateTime(v.cancelledAt)}</span>
              {v.cancelledReason && <span className="text-ink-400"> · {v.cancelledReason}</span>}
            </span>
          )}
        </div>
      </div>
      {isActive && (
        <button
          type="button"
          onClick={() => onCancel(v)}
          className="btn-sm text-joy-700 hover:bg-joy-50 rounded-lg px-3 py-1.5 font-semibold transition-colors inline-flex items-center gap-1.5"
          aria-label="ביטול שובר"
        >
          <ArchiveBoxXMarkIcon className="w-4 h-4" />
          ביטול
        </button>
      )}
    </li>
  );
}

/* ───── Empty / pagination ───────────────────────────────────── */

function EmptyState({ hasFilters, onCreate }) {
  return (
    <div className="card-pop p-10 sm:p-14 text-center max-w-xl mx-auto">
      <div className="mx-auto w-16 h-16 rounded-3xl bg-gold-gradient text-white grid place-items-center mb-4 shadow-pop">
        <CreditCardIcon className="w-8 h-8" />
      </div>
      <h2 className="text-lg font-bold text-ink-900">
        {hasFilters ? 'אין תוצאות לסינון' : 'אין עדיין שוברים'}
      </h2>
      {!hasFilters && (
        <>
          <p className="mt-2 text-muted">
            צור סדרה: בחר כמה שוברים וערך לכל אחד. הקודים יוגרלו אוטומטית
            ותוכל להדפיס אותם לכיתות.
          </p>
          <button type="button" className="btn-pop btn-lg mt-5" onClick={onCreate}>
            <PlusIcon className="w-5 h-5" />
            סדרה ראשונה
          </button>
        </>
      )}
    </div>
  );
}

function Pagination({ page, pageSize, total, hasMore, onPrev, onNext }) {
  if (total <= pageSize && page === 0) return null;
  const from = page * pageSize + 1;
  const to = Math.min((page + 1) * pageSize, total);
  return (
    <div className="flex items-center justify-between text-sm text-ink-600">
      <div className="num">מציג {from}-{to} מתוך {total}</div>
      <div className="flex gap-2">
        <button type="button" className="btn-secondary btn-sm" onClick={onPrev} disabled={page === 0}>קודם</button>
        <button type="button" className="btn-secondary btn-sm" onClick={onNext} disabled={!hasMore}>הבא</button>
      </div>
    </div>
  );
}

/* ───── Create batch dialog ──────────────────────────────────── */

function CreateBatchDialog({ open, onClose, onSubmit, pending, currency, campId }) {
  const [count, setCount] = useState(50);
  const [amount, setAmount] = useState(10);
  const [batchLabel, setBatchLabel] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [error, setError] = useState(null);
  // The just-created batch's identity (label + createdAt), or null before
  // create. An OBJECT (not the label) so the success UI also shows for an
  // unlabelled batch (label is null) and the print link can pin the exact
  // create by createdAt — not every same-named series.
  const [createdBatch, setCreatedBatch] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    const body = {
      count:      parseInt(count, 10),
      amount:     parseInt(amount, 10),
      batchLabel: batchLabel.trim() || undefined,
      expiresAt:  expiresAt ? new Date(expiresAt).toISOString() : undefined,
    };
    try {
      const result = await onSubmit(body);
      // Show the print link for the just-created batch, pinned by its exact
      // identity (label + createdAt) so it prints ONLY this create.
      setCreatedBatch({
        batchLabel: result?.batchLabel ?? null,
        createdAt:  result?.createdAt ?? null,
      });
    } catch (err) {
      setError(err.message || err.code || 'שגיאה לא צפויה');
    }
  }

  // Reset form when modal opens fresh.
  function reset() {
    setCount(50); setAmount(10); setBatchLabel(''); setExpiresAt('');
    setError(null); setCreatedBatch(null);
  }

  // Print link for the just-created batch — createdAt pins the one create.
  function createdPrintHref() {
    const qs = new URLSearchParams({ status: 'ACTIVE' });
    if (createdBatch?.batchLabel) qs.set('batch', createdBatch.batchLabel);
    if (createdBatch?.createdAt)  qs.set('createdAt', createdBatch.createdAt);
    return `/api/camps/${campId}/vouchers/print.html?${qs.toString()}`;
  }

  if (!open) return null;
  return (
    <Modal open={open} onClose={() => { reset(); onClose(); }} title="יצירת סדרה חדשה" maxWidth="max-w-lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-muted">
          הגדר כמות, ערך, ותווית אופציונלית. הקודים יוגרלו אקראית
          ויהיו ייחודיים בתכנית.
        </p>

        {error && (
          <div className="rounded-2xl bg-joy-50 border border-joy-200 text-joy-900 px-4 py-3 text-sm font-medium flex items-start gap-2 animate-slide-up">
            <ExclamationTriangleIcon className="w-5 h-5 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="label">כמות שוברים <span className="text-joy-600">*</span></span>
            <input
              type="number"
              inputMode="numeric"
              className="input"
              value={count}
              onChange={(e) => setCount(e.target.value)}
              min={1}
              max={5000}
              required
            />
            <span className="hint">עד 5000 לסדרה</span>
          </label>
          <label className="block">
            <span className="label">ערך לשובר <span className="text-joy-600">*</span></span>
            <input
              type="number"
              inputMode="numeric"
              className="input"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min={1}
              max={9999}
              required
            />
            <span className="hint">בנקודות / ש״ח לפי המטבע של התכנית</span>
          </label>
        </div>

        <label className="block">
          <span className="label">שם הסדרה</span>
          <input
            type="text"
            className="input"
            value={batchLabel}
            onChange={(e) => setBatchLabel(e.target.value)}
            maxLength={60}
          />
          <span className="hint">אופציונלי. עוזר לסנן ולהדפיס סדרה אחת בלבד.</span>
        </label>

        <label className="block">
          <span className="label">תאריך תפוגה</span>
          <input
            type="date"
            className="input"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
          <span className="hint">אופציונלי. ללא בחירה — השובר ללא תפוגה.</span>
        </label>

        {createdBatch ? (
          <div className="rounded-2xl bg-mint-50 border border-mint-200 p-4 space-y-3">
            <div className="text-sm text-mint-900 font-semibold">
              ✓ הסדרה נוצרה. עכשיו אפשר להדפיס.
            </div>
            <div className="flex flex-wrap gap-2">
              <a
                href={createdPrintHref()}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                הדפסת השוברים
              </a>
              <button type="button" className="btn-secondary" onClick={() => { reset(); onClose(); }}>
                סגירה
              </button>
            </div>
          </div>
        ) : (
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-secondary" onClick={() => { reset(); onClose(); }}>
              ביטול
            </button>
            <button type="submit" className="btn-pop" disabled={pending}>
              {pending ? 'יוצר...' : 'צור סדרה'}
            </button>
          </div>
        )}
      </form>
    </Modal>
  );
}

/* ───── Cancel form ──────────────────────────────────────────── */

function CancelForm({ voucher, currency, pending, onCancel, onConfirm }) {
  const [reason, setReason] = useState('');
  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-700">
        לבטל את השובר <strong dir="ltr" className="font-mono num">{groupCode(voucher.code)}</strong>{' '}
        בערך <strong className="num">{formatMoney(voucher.amount, currency)}</strong>?
        אחרי הביטול לא ניתן יהיה לממש אותו.
      </p>
      <label className="block">
        <span className="label">סיבה (אופציונלי)</span>
        <input
          type="text"
          className="input"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={200}
        />
      </label>
      <div className="flex justify-end gap-2">
        <button type="button" className="btn-secondary" onClick={onCancel}>חזרה</button>
        <button
          type="button"
          className="btn-danger"
          disabled={pending}
          onClick={() => onConfirm(reason.trim() || undefined)}
        >
          {pending ? 'מבטל...' : 'כן, בטל'}
        </button>
      </div>
    </div>
  );
}

function SearchIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M10 17a7 7 0 100-14 7 7 0 000 14z" />
    </svg>
  );
}

function PlusIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  );
}
